import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Crosshair, FileCode2, Inbox, LogOut, Newspaper, Radar, Rss, ScrollText, ShieldAlert, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageHeader, Tabs, TabList, Tab, TabPanel } from '@/components/primitives';
import CompaniesTab from '@/pages/admin/CompaniesTab';
import CampaignsTab from '@/pages/admin/CampaignsTab';
import AutoIocsTab from '@/pages/admin/AutoIocsTab';
import FeedsTab from '@/pages/admin/FeedsTab';
import RulesTab from '@/pages/admin/RulesTab';
import InsightsTab from '@/pages/admin/InsightsTab';
import BreachTab from '@/pages/admin/BreachTab';
import AuditTab from '@/pages/admin/AuditTab';
import DarkWebTab from '@/pages/admin/DarkWebTab';
import { adminApi, setAuthLostHandler, useAdminQuery } from '@/pages/admin/adminApi';
import '@/styles/admin-console.css';

const TAB_KEY = 'admin_tab';
const TABS = ['companies', 'campaigns', 'auto-iocs', 'darkweb', 'feeds', 'rules', 'insights', 'breach', 'audit'];

const readTab = () => {
  try {
    const t = localStorage.getItem(TAB_KEY);
    return TABS.includes(t) ? t : 'companies';
  } catch {
    return 'companies';
  }
};

/**
 * Platform operator console. Each tab lives in src/pages/admin/; this page
 * only owns the session (a rejected admin token sends you back to sign-in)
 * and the review-queue counts shown on the tabs.
 */
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(readTab);
  const status = useAdminQuery(() => adminApi.get('/admin/system/status').then((r) => r.data), []);

  useEffect(() => {
    setAuthLostHandler(() => navigate('/admin/login', { replace: true }));
    return () => setAuthLostHandler(null);
  }, [navigate]);

  const changeTab = (next) => {
    setTab(next);
    try { localStorage.setItem(TAB_KEY, next); } catch { /* storage blocked */ }
  };

  const logout = () => {
    try { localStorage.removeItem('admin_token'); } catch { /* storage blocked */ }
    navigate('/admin/login', { replace: true });
  };

  const counts = status.data || {};
  const refreshCounts = () => status.reload();

  return (
    <div className="adm-page" data-testid="admin-dashboard">
      <PageHeader
        title="Admin console"
        subtitle="Tenants, content moderation, feeds and the audit trail."
        icon={Shield}
        actions={<Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout-btn"><LogOut aria-hidden="true" /> Sign out</Button>}
      />

      <Tabs value={tab} onValueChange={changeTab}>
        <TabList label="Admin sections">
          <Tab value="companies" icon={Building2} count={counts.companies}>Tenants</Tab>
          <Tab value="campaigns" icon={Crosshair} count={counts.pending_campaigns || undefined}>Hunt campaigns</Tab>
          <Tab value="auto-iocs" icon={Inbox} count={counts.pending_auto_iocs || undefined}>IOC review</Tab>
          <Tab value="darkweb" icon={Radar} count={counts.pending_darkweb || undefined}>Dark web</Tab>
          <Tab value="feeds" icon={Rss}>Feeds</Tab>
          <Tab value="rules" icon={FileCode2}>Rules</Tab>
          <Tab value="insights" icon={Newspaper}>Insights</Tab>
          <Tab value="breach" icon={ShieldAlert}>Breach</Tab>
          <Tab value="audit" icon={ScrollText}>Audit log</Tab>
        </TabList>

        <TabPanel value="companies"><CompaniesTab onChanged={refreshCounts} /></TabPanel>
        <TabPanel value="campaigns"><CampaignsTab onChanged={refreshCounts} /></TabPanel>
        <TabPanel value="auto-iocs"><AutoIocsTab onChanged={refreshCounts} /></TabPanel>
        <TabPanel value="darkweb"><DarkWebTab onChanged={refreshCounts} /></TabPanel>
        <TabPanel value="feeds"><FeedsTab /></TabPanel>
        <TabPanel value="rules"><RulesTab /></TabPanel>
        <TabPanel value="insights"><InsightsTab /></TabPanel>
        <TabPanel value="breach"><BreachTab /></TabPanel>
        <TabPanel value="audit"><AuditTab /></TabPanel>
      </Tabs>
    </div>
  );
}
